import type { ReactElement, ReactNode } from 'react';
import { Notice as UiNotice } from '@/components/ui/Notice';
import { formatDecimalString } from '@/lib/format';
import { UNCODED_MEANING } from './revenue-product';

/**
 * EVERYTHING THIS TAB HAS TO SAY ABOUT WHAT IS **NOT** IN THE FIGURES BELOW.
 *
 * ⚠️ **LIFTED OUT OF THE CONTROL BAR, WHICH IS THE WHOLE POINT OF THE FILE.**
 * These six sentences used to hang off the bottom of the filter panel as six
 * conditional `<p>` rows, so the controls and the caveats about the controls
 * were one undifferentiated wall — the thing the owner means by *a mess*. They
 * are not controls. They are measurements, and a measurement belongs beside the
 * figures it qualifies rather than inside the thing that produced it.
 *
 * ── 🔴 NOT ONE OF THESE MAY BE DROPPED TO SHORTEN THE PAGE ─────────────────
 *
 * Every one names money or products that are **in no figure on the page**, and
 * each is silent in the same direction: the page looks complete without it.
 * A shorter table, a smaller total and an absent caveat are indistinguishable
 * from a quiet quarter.
 *
 * ⚠️ **THEY RENDER ONLY WHEN TRUE, AND THE COMPONENT RETURNS `null` WHEN NONE
 * IS.** On a healthy read in USD that is the ordinary state, so this costs no
 * vertical space in the common case — which is what makes it affordable to show
 * every one of them prominently in the uncommon case.
 *
 * ── ⚠️ THE ORDER IS BY CONSEQUENCE, NOT BY CATEGORY ────────────────────────
 *
 * Stale figures first (everything below them may be wrong), then the four that
 * remove money from the totals, then the one that removes products from the
 * list. A reader who stops after the first has stopped at the most important.
 *
 * ⚠️ **The fourth money notice — lines with no exchange rate for their own bill
 * date — arrived with conversion on 2026-09-14**, and the last one
 * (`excludedByCurrency`) is unconverted-only from that day: its sentence names a
 * currency FILTER that no longer exists on this tab, so the panel passes `0`
 * rather than letting it draw. **Count the branches below, never this
 * paragraph.**
 */

/**
 * ⚠️ **`warning`, NEVER `danger`.** Nothing here has failed and nothing on
 * screen is wrong — money is missing from a total for a reason that is recorded
 * and explained. A red band sends somebody looking for a broken page instead of
 * for an invoice with no currency on it.
 */
function Notice({
  tone,
  children,
}: {
  tone: 'warning' | 'muted';
  children: ReactNode;
}): ReactElement {
  /*
   * Delegates to the shared `ui/Notice` (2026-10-11) so these read like every
   * other notice in the app; `role="status"` is kept, as it was.
   */
  return (
    <UiNotice tone={tone === 'warning' ? 'warning' : 'neutral'} role="status">
      {children}
    </UiNotice>
  );
}

export function RevenueProductNotices({
  currency,
  refreshError,
  uncodedExcluded,
  unstatedProducts,
  unmatchedLines,
  excludedByCurrency,
  unpricedLines,
}: {
  /**
   * ⚠️ **THE UNIT THE FIGURES ARE IN — the server's echoed
   * `meta.measuredInCurrency` where it converted, the picker's value where it
   * did not.** Never the string that was requested: these sentences name a
   * currency beside an amount, and naming the wrong one is the failure the echo
   * exists to prevent.
   */
  currency: string;
  /**
   * A refresh that failed over figures already on screen. `null` when the read
   * is healthy, or when it failed with nothing behind it — that case replaces
   * the whole tab and never reaches here.
   */
  refreshError: string | null;
  /**
   * The uncatalogued bucket's total **while it is filtered out**, or `null`
   * while it is listed.
   *
   * 🔴 **THIS IS THE MOST CONSEQUENTIAL CONTROL ON THE TAB AND IT LIVES BEHIND A
   * DISCLOSURE.** Measured on production 2026-09-11: the bucket is **USD
   * 5,147,358.44 of 8,527,-odd**, so excluding it removes about three fifths of
   * the money from every figure on the page. The count on the collapsed
   * *Filters* toggle says that *something* is set; this says **what**, and what
   * it costs. Neither substitutes for the other.
   */
  uncodedExcluded: string | null;
  /** Products carrying revenue with no currency recorded against it. */
  unstatedProducts: number;
  /** Paid lines the server could not place in any cycle. */
  unmatchedLines: number;
  /**
   * Products that took nothing at all in the chosen currency.
   *
   * ⚠️ **UNCONVERTED ONLY — the panel passes `0` under conversion.** Its sentence
   * ends *"they may have sold in another currency"*, which was true while the
   * currency control filtered and is a false statement now that every currency
   * is in the figures.
   */
  excludedByCurrency: number;
  /**
   * Paid lines with **no exchange rate stored for their own bill date**, and
   * therefore in none of the figures below.
   *
   * 🔴 **`null` IS "NOTHING WAS EXAMINED", NOT ZERO** — the unconverted path, and
   * a server predating the key. Both render nothing, which is exactly why the
   * distinction has to live in the TYPE: a `?? 0` here would read as an
   * all-clear to the next person and nothing would ever contradict it.
   *
   * ⚠️ **NOT A SECOND COPY OF {@link unmatchedLines}.** One is work that fell in
   * no cycle column; this is work that could not be put into one unit. A line can
   * be neither, either or both.
   */
  unpricedLines: number | null;
}): ReactElement | null {
  const anything =
    refreshError !== null ||
    uncodedExcluded !== null ||
    unstatedProducts > 0 ||
    unmatchedLines > 0 ||
    excludedByCurrency > 0 ||
    (unpricedLines !== null && unpricedLines > 0);

  if (!anything) return null;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {/*
       * ⚠️ **THE FIGURES BELOW ARE STALE RATHER THAN ABSENT, and the honest
       * render is both** — they stay on screen, and this says why they are old.
       * Replacing them would throw away numbers that were true a moment ago.
       */}
      {refreshError !== null && (
        <Notice tone="warning">
          <span className="font-semibold">These figures could not be refreshed.</span>{' '}
          {refreshError}
        </Notice>
      )}

      {/*
       * 🔴 **THE ONE SENTENCE THAT STOPS A THREE-FIFTHS-SHORT TOTAL BEING READ
       * AS THE BUSINESS'S REVENUE.** It states the AMOUNT and deliberately not a
       * percentage: with the bucket excluded the total beside it no longer
       * contains the bucket, so any share taken against that total is a share of
       * the wrong whole. An amount needs no denominator to be true.
       */}
      {uncodedExcluded !== null && (
        <Notice tone="warning">
          {/*
           * ⚠️ **THE CONSEQUENCE IS IN THE BOLD AND THE DEFINITION IS NOT.** A
           * reader who takes only the emphasised clause has taken the thing that
           * changes what the page means; leading with what the bucket IS would
           * put a definition where the warning belongs and bury the amount.
           */}
          {/*
           * ⛔ **THIS AMOUNT IS EXACT AND IS DELIBERATELY NOT COMPACTED.** The
           * 2026-09-13 owner decision put `£20.00K` on every money figure of
           * the Management Dashboard; it was applied to the headline tile and
           * the revenue column of this tab and STOPPED HERE, on purpose.
           *
           * **A gap figure is not a scope figure.** Everything compaction is
           * for — four tiles aligning, a money column that does not wrap, a
           * number the eye takes in at a glance — applies to a quantity the
           * reader is SCANNING. This one is a quantity they are RECONCILING:
           * the sentence exists so somebody can check this page against Xero,
           * against a spreadsheet, against what they were told last month. A
           * rounded answer to *"how much is missing"* has thrown away up to
           * £5,000 of the very thing it is reporting as missing, so a genuine
           * £4,000 discrepancy reads as agreement — the measure of an error is
           * the last number in a system that may be rounded.
           *
           * ⚠️ **AND A `title` CANNOT RESCUE A SENTENCE.** The tile's figure
           * has the table beneath it as a second route to the exact number;
           * this has none, and a warning is the thing that gets screenshotted,
           * pasted into an email and read aloud — none of which carries a hover
           * attribute with them.
           *
           * ⚠️ **Consistent across the tab: every GAP amount is exact, every
           * SCOPE amount is compact** — the two sub-lines of the Uncatalogued
           * tile in `RevenueProductHeadline` are kept exact for the same
           * reason, with the argument written out in full at the first of them.
           * The rule is what a figure is FOR, never where it renders, so a
           * later sweep must not "finish" the compaction by taking these three.
           */}
          <span className="font-semibold">
            Uncatalogued revenue is excluded — {currency}{' '}
            {formatDecimalString(uncodedExcluded)} of paid revenue in this scope is in no figure
            on this page.
          </span>{' '}
          {UNCODED_MEANING}
        </Notice>
      )}

      {/*
       * 🔴 **A FIGURE WHOSE UNIT WAS NEVER RECORDED IS IN NONE OF THE TOTALS.**
       * `d_invoices.currency` is nullable, so a real amount can arrive with no
       * currency against it. Labelling it with whichever currency the reader
       * happens to have selected would invent a fact about money, so it is
       * withheld — and withholding it silently would make this page under-count
       * with nothing on screen to show it.
       */}
      {unstatedProducts > 0 && (
        <Notice tone="warning">
          <span className="font-semibold">
            {unstatedProducts.toLocaleString()} product{unstatedProducts === 1 ? '' : 's'} carr
            {unstatedProducts === 1 ? 'ies' : 'y'} revenue with no currency recorded.
          </span>{' '}
          Those figures are in none of the totals below, because there is no unit to state them
          in.
        </Notice>
      )}

      {/*
       * 🔴 **THE CYCLES DO NOT SUM TO THE BUSINESS'S REVENUE WHILE THIS IS ABOVE
       * ZERO.** These are paid lines the server could not place in any cycle — an
       * absent or out-of-range bill date — so they are in no column anywhere on
       * this tab, including the "All cycles" scope, which is not a synonym for
       * "everything".
       */}
      {unmatchedLines > 0 && (
        <Notice tone="warning">
          <span className="font-semibold">
            {unmatchedLines.toLocaleString()} paid{' '}
            {unmatchedLines === 1 ? 'line falls' : 'lines fall'} outside every cycle.
          </span>{' '}
          They are in none of the figures below.
        </Notice>
      )}

      {/*
       * 🔴 **THE ONE THING THAT KEEPS CONVERSION HONEST — added 2026-09-14 with
       * it.** A paid line whose own bill date has no stored exchange rate is left
       * OUT of every figure rather than priced at par: converting at 1.0 would
       * put dollars into a sterling total as though the two were the same money.
       * Without this sentence the tab silently under-reports and looks complete,
       * which is the failure every notice in this file exists to prevent.
       *
       * ⛔ **"COULD NOT BE CONVERTED", NEVER "IN ANOTHER CURRENCY", AND THE TWO
       * SEND A READER TO OPPOSITE PLACES.** A foreign line IS converted now, so
       * the old wording names a policy that no longer exists and tells somebody
       * nothing can be done. This names **missing reference data**, which a
       * person closes by backfilling the rate for that date — hence the closing
       * clause, which is the whole actionable half. The same correction
       * `excludedByCurrency` on `/targets/actuals` had to be made, for the same
       * reason.
       *
       * ⚠️ **EXACT, NOT COMPACTED, AND A COUNT RATHER THAN AN AMOUNT.** The
       * amount is not on the wire, and it could not be: a line that cannot be
       * priced has no figure in this unit to state. A count is the honest answer
       * and it is what somebody takes to the rate table.
       *
       * ⚠️ **`null` RENDERS NOTHING AND SO DOES `0`, FOR DIFFERENT REASONS** —
       * nothing was examined, against nothing was lost. Tested explicitly rather
       * than through truthiness, so the distinction survives in the code even
       * though it does not survive on the screen.
       */}
      {unpricedLines !== null && unpricedLines > 0 && (
        <Notice tone="warning">
          <span className="font-semibold">
            {unpricedLines.toLocaleString()} paid{' '}
            {unpricedLines === 1 ? 'line could' : 'lines could'} not be converted into {currency}.
          </span>{' '}
          There is no exchange rate stored for {unpricedLines === 1 ? 'its' : 'their'} own bill
          date, so {unpricedLines === 1 ? 'it is' : 'they are'} in none of the figures below. The
          rate is missing, not the revenue.
        </Notice>
      )}

      {/*
       * 🔴 **SWITCHING CURRENCY HIDES PRODUCTS, AND THIS IS THE ONLY THING THAT
       * SAYS SO.** Measured on production: 652 USD invoices against 89 EUR and 56
       * GBP, so a currency that is not USD legitimately drops most of the
       * catalogue. Without this line a short list reads as a catalogue nobody is
       * buying.
       *
       * ⚠️ **`muted`, unlike the three above.** Nothing is missing from a total
       * here — those products took no money in this unit, which is a fact rather
       * than a gap. Toning it like the others would spend the reader's alarm on
       * the one notice that is not about missing money.
       */}
      {excludedByCurrency > 0 && (
        <Notice tone="muted">
          <span className="font-medium text-fg">
            {excludedByCurrency.toLocaleString()}{' '}
            {excludedByCurrency === 1 ? 'product took' : 'products took'} no {currency}
          </span>{' '}
          and {excludedByCurrency === 1 ? 'is' : 'are'} not listed. They may have sold in another
          currency.
        </Notice>
      )}
    </div>
  );
}
